import { FirebaseError } from 'firebase/app'
import {
  AuthCredential,
  getRedirectResult,
  GoogleAuthProvider,
  linkWithCredential,
  OAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  User,
  UserCredential,
} from 'firebase/auth'
import { getFirebaseClient, SocialProviderId } from './firebaseClient'

export const DEFAULT_SIGN_IN_ERROR = 'Please double check that both your email and password are typed correctly.'
export const EMAIL_NOT_CONFIRMED_ERROR =
  'Your email is not yet confirmed. Please confirm it from the email we sent you, then try again.'

// Raised when the email already has an Awair account under another sign-in method. The caller asks
// for that account's password and calls linkPendingCredential() so the user lands in the same account.
export class AccountConflictError extends Error {
  constructor(readonly email: string, readonly pendingCredential: AuthCredential, readonly providerName: string) {
    super(`An account already exists for ${email}. Enter its password to link ${providerName} and continue.`)
    this.name = 'AccountConflictError'
  }
}

const PROVIDER_NAMES: Record<SocialProviderId, string> = {
  google: 'Google',
  apple: 'Apple',
}

export function getFirebaseErrorMessage(error: unknown): string {
  const code = error instanceof FirebaseError ? error.code : ''
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-email':
      return DEFAULT_SIGN_IN_ERROR
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.'
    case 'auth/user-disabled':
      return 'This account has been disabled.'
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.'
    case 'auth/popup-blocked':
      return 'The sign-in window was blocked. Please allow pop-ups and try again.'
    case 'auth/unauthorized-domain':
    case 'auth/operation-not-allowed':
      return 'This sign-in option is not available right now. Please sign in with your email and password.'
    default:
      return 'Please try again.'
  }
}

function isPopupDismissed(error: unknown): boolean {
  return (
    error instanceof FirebaseError &&
    (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request')
  )
}

function shouldFallBackToRedirect(error: unknown): boolean {
  return (
    error instanceof FirebaseError &&
    (error.code === 'auth/popup-blocked' || error.code === 'auth/operation-not-supported-in-this-environment')
  )
}

function toAccountConflict(error: unknown): AccountConflictError | null {
  if (!(error instanceof FirebaseError) || error.code !== 'auth/account-exists-with-different-credential') {
    return null
  }
  const email = (error.customData?.email as string | undefined) ?? ''
  // The generic extractor keeps the issuing provider (and Apple's nonce); GoogleAuthProvider's would
  // label any OAuth tokens as google.com.
  const pendingCredential =
    OAuthProvider.credentialFromError(error) ?? GoogleAuthProvider.credentialFromError(error)
  if (!email || !pendingCredential) {
    return null
  }
  const providerName = pendingCredential.providerId === 'apple.com' ? PROVIDER_NAMES.apple : PROVIDER_NAMES.google
  return new AccountConflictError(email, pendingCredential, providerName)
}

// Password sign-in requires a confirmed email, matching the legacy /v1:authorize check, whatever
// other providers are linked to the account.
async function requireConfirmedEmail(user: User): Promise<void> {
  if (!user.emailVerified) {
    await signOutFirebase()
    throw new Error(EMAIL_NOT_CONFIRMED_ERROR)
  }
}

// Returns a fresh ID token for /v1:firebaseAuthorize.
function idTokenFor(user: User): Promise<string> {
  return user.getIdToken(true)
}

function rethrow(error: unknown): never {
  const conflict = toAccountConflict(error)
  if (conflict) {
    throw conflict
  }
  if (error instanceof FirebaseError) {
    throw new Error(getFirebaseErrorMessage(error))
  }
  throw error
}

export class FirebaseAuthService {
  static async signInWithPassword(email: string, password: string): Promise<string> {
    const { auth } = getFirebaseClient()
    let credential: UserCredential
    try {
      credential = await signInWithEmailAndPassword(auth, email.trim(), password)
    } catch (error) {
      rethrow(error)
    }
    await requireConfirmedEmail(credential.user)
    return idTokenFor(credential.user)
  }

  // Resolves to null when the user closes the popup, or when the page is about to redirect to the
  // provider because popups are blocked (completeRedirectSignIn() picks it up after the round-trip).
  static async signInWithProvider(providerId: SocialProviderId): Promise<string | null> {
    const { auth, providers } = getFirebaseClient()
    let credential: UserCredential
    try {
      credential = await signInWithPopup(auth, providers[providerId])
    } catch (error) {
      if (isPopupDismissed(error)) {
        return null
      }
      if (shouldFallBackToRedirect(error)) {
        try {
          await signInWithRedirect(auth, providers[providerId])
        } catch (redirectError) {
          rethrow(redirectError)
        }
        return null
      }
      rethrow(error)
    }
    return idTokenFor(credential.user)
  }

  static async completeRedirectSignIn(): Promise<string | null> {
    const { auth } = getFirebaseClient()
    let credential: UserCredential | null
    try {
      credential = await getRedirectResult(auth)
    } catch (error) {
      rethrow(error)
    }
    return credential ? idTokenFor(credential.user) : null
  }

  static async linkPendingCredential(conflict: AccountConflictError, password: string): Promise<string> {
    const { auth } = getFirebaseClient()
    let credential: UserCredential
    try {
      credential = await signInWithEmailAndPassword(auth, conflict.email, password)
    } catch (error) {
      rethrow(error)
    }
    // Don't attach the provider to an account that couldn't sign in with its password today.
    await requireConfirmedEmail(credential.user)
    try {
      await linkWithCredential(credential.user, conflict.pendingCredential)
    } catch (error) {
      rethrow(error)
    }
    return idTokenFor(credential.user)
  }

  // Continue URL brings the user back to the sign-in page with the original OAuth query string.
  static async sendPasswordReset(email: string, continueUrl: string): Promise<void> {
    const { auth } = getFirebaseClient()
    try {
      await sendPasswordResetEmail(auth, email.trim(), { url: continueUrl })
    } catch (error) {
      // Same neutral outcome as an existing account, so the page doesn't reveal which emails exist.
      if (error instanceof FirebaseError && error.code === 'auth/user-not-found') {
        return
      }
      rethrow(error)
    }
  }

  static async signOutFirebase(): Promise<void> {
    await signOutFirebase()
  }
}

async function signOutFirebase(): Promise<void> {
  try {
    await signOut(getFirebaseClient().auth)
  } catch (error) {
    console.log('[signOutFirebase()]' + String(error))
  }
}
